import { Factory, Model } from 'miragejs'
import { getQueryParamValue } from '@/api/mock-server/utils.js'
import { randMask } from '@ngneat/falso'

import type { ModelDefinition } from 'miragejs/-types'
import type { MirageServer, MockServer } from '@/api/mock-server/server'
import type { ReportSummaryTimePeriod } from '@/components/reports/summary/config'
import type {
  SummaryRevenueData,
  SummaryRevenueDataBE,
} from '@/composables/api/ReportsSummary.js'
import { safeMathRandom } from '@/utils/number.js'

export const model = Model as ModelDefinition<SummaryRevenueData>

const NR_OF_SERIES_ITEMS = 6

export const factory = Factory.extend<SummaryRevenueDataBE>({
  id() {
    return randMask({ mask: '################' })
  },
  current_total() {
    return {
      id: randMask({ mask: '################' }),
      period_label: 'current',
      value: Math.floor(safeMathRandom() * 10000) + 1,
    }
  },
  previous_total() {
    return {
      id: randMask({ mask: '################' }),
      period_label: 'previous',
      value: Math.floor(safeMathRandom() * 10000) + 1,
    }
  },
  totals_delta() {
    return parseFloat((safeMathRandom() * 40 - 20).toFixed(1))
  },
  current_series() {
    return Array.from({ length: NR_OF_SERIES_ITEMS }, (_, i) => ({
      id: randMask({ mask: '################' }),
      index_label: String(i),
      real_label: ` ${i + 1}`,
      value: Math.floor(safeMathRandom() * 10000) + 1,
    }))
  },
  previous_series() {
    return Array.from({ length: NR_OF_SERIES_ITEMS }, (_, i) => ({
      id: randMask({ mask: '################' }),
      index_label: String(i),
      real_label: ` ${i + 1}`,
      value: Math.floor(safeMathRandom() * 10000) + 1,
    }))
  },
})

export function routes(server: MockServer) {
  server.get(
    '/reports_v2/practice_performance_revenue',
    function (this: MirageServer, schema, request) {
      const period_type = getQueryParamValue(
        request?.queryParams?.period_type,
      ) as ReportSummaryTimePeriod['type']
      const periodTypeLowerCase = period_type.toLowerCase() as Lowercase<
        typeof period_type
      >

      const summary = server.create('summaryRevenue')

      // Adjust label based on period
      const periodValues = {
        day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        month: ['May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'],
        week: [
          '18-24 Aug',
          '25-31 Aug',
          '1-7 Sep',
          '8-14 Sep',
          '15-21 Sep',
          '22 Sep-28 Sep',
          '29 Sep-5 Oct',
        ],
      }

      const mockedMonthValues = {
        current: [25000, 24000, 28000, 27000, 26000, 23880],
        previous: [17000, 16000, 20000, 22000, 19000, 28100],
      }

      summary.attrs.current_series.forEach(
        (
          item: SummaryRevenueDataBE['current_series'][number],
          index: number,
        ) => {
          item.real_label = periodValues[periodTypeLowerCase][index]
          item.value = mockedMonthValues.current[index]
        },
      )

      summary.attrs.previous_series.forEach(
        (
          item: SummaryRevenueDataBE['previous_series'][number],
          index: number,
        ) => {
          item.real_label = periodValues[periodTypeLowerCase][index]
          item.value = mockedMonthValues.previous[index]
        },
      )

      summary.attrs.current_total.value =
        summary.attrs.current_series[NR_OF_SERIES_ITEMS - 1].value

      summary.attrs.previous_total.value =
        summary.attrs.previous_series[NR_OF_SERIES_ITEMS - 1].value

      summary.attrs.totals_delta =
        summary.attrs.previous_total.value === 0
          ? null
          : Number(
              (
                ((summary.attrs.current_total.value -
                  summary.attrs.previous_total.value) /
                  summary.attrs.previous_total.value) *
                100
              ).toFixed(1),
            )

      return summary
    },
    { timing: 900 },
  )
}

export function seeds(server: MockServer) {
  server.create('summaryRevenue')
}
